import { useTranslation } from "react-i18next";
import type { QuoteEstimate } from "../../features/quotes/types";
import { Card } from "../ui";

export function formatMoney(amount: number | null | undefined, currency = "MXN"): string {
  const n = Number(amount ?? 0);
  return `$${n.toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency}`;
}

export function formatDuration(minutes: number | null | undefined): string {
  const m = Math.max(0, Math.round(Number(minutes) || 0));
  if (m === 0) return "—";
  const h = Math.floor(m / 60);
  const r = m % 60;
  if (h === 0) return `${r} min`;
  return r ? `${h} h ${r} min` : `${h} h`;
}

interface QuoteTotalsProps {
  estimate: Pick<QuoteEstimate, "cost_total" | "margin_pct" | "price" | "taxes" | "total_with_tax" | "currency" | "cost_per_km">;
  compact?: boolean;
}

/** Costo → margen → precio → impuestos → total. */
export function QuoteTotals({ estimate, compact = false }: QuoteTotalsProps) {
  const { t } = useTranslation("finanzasOperativas");
  const { currency } = estimate;
  const rows: { label: string; value: string; strong?: boolean; muted?: boolean }[] = [
    { label: t("quotes.fields.costTotal"), value: formatMoney(estimate.cost_total, currency) },
    { label: t("quotes.fields.margin"), value: `${estimate.margin_pct.toFixed(2)} %`, muted: true },
    { label: t("quotes.fields.price"), value: formatMoney(estimate.price, currency), strong: true },
  ];
  if (estimate.taxes.iva_amount) rows.push({ label: `${t("quotes.fields.iva")} ${estimate.taxes.iva_pct}%`, value: formatMoney(estimate.taxes.iva_amount, currency), muted: true });
  if (estimate.taxes.retention_amount)
    rows.push({ label: `${t("quotes.fields.retention")} ${estimate.taxes.retention_pct}%`, value: `− ${formatMoney(estimate.taxes.retention_amount, currency)}`, muted: true });
  rows.push({ label: t("quotes.fields.total"), value: formatMoney(estimate.total_with_tax, currency), strong: true });

  return (
    <Card padding={compact ? "sm" : "md"}>
      <dl className="space-y-1.5">
        {rows.map((r) => (
          <div key={r.label} className="flex items-baseline justify-between gap-4">
            <dt className={`text-xs ${r.muted ? "text-content-faint" : "text-content-secondary"}`}>{r.label}</dt>
            <dd className={`tabular-nums ${r.strong ? "text-base font-semibold text-content" : "text-sm text-content-secondary"}`}>{r.value}</dd>
          </div>
        ))}
        {estimate.cost_per_km > 0 && (
          <div className="flex items-baseline justify-between gap-4 border-t border-line pt-1.5">
            <dt className="text-xs text-content-faint">{t("quotes.fields.costPerKm")}</dt>
            <dd className="text-xs tabular-nums text-content-faint">{formatMoney(estimate.cost_per_km, currency)}</dd>
          </div>
        )}
      </dl>
    </Card>
  );
}
